import logo from "assets/images/header_logo.png";
import backbutton from "assets/images/back_arrow_white.png";
import { Image } from "components/Image";
import { useGuidanceStore } from "@/stores/guidanceStore";
import {
  getUrlPathBasedOnIndex,
  pushToDataLayer,
} from "core/utils/utils.helper";
import { routes } from "constants/routes";
import { useState, useEffect } from "react";
import useMediaQuery from "hooks/useMediaQuery";
import { ScreenSize } from "models/screenSize";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { menuItems } from "constants/general";
import profileIcon from "assets/images/profile_white_icon.svg";
import { CONTENT } from "constants/dataLayer";
import useAuth from "hooks/useAuth";
import useProfileImage from "hooks/useProfileImage";
import { Button } from "components/Button";
import { BTN_SIZE, BTN_VARIANTS } from "constants/components";
import { handleOnboardingBackNavigation } from "@/helpers/onboarding.helper";
import styles from "./Header.module.scss";

export const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Zustand store - replaces useRecoilValue(guidanceDetailsProgressAtom)
  const progressVal = useGuidanceStore((state) => state.progressValue);
  const selectedKey_1 = getUrlPathBasedOnIndex(2) ?? ""; // Default to empty string if undefined
  const selectedKey_3 = getUrlPathBasedOnIndex(3) ?? ""; // get guidance category id to show progress bar
  const [isBackButtonVisible, setIsBackButtonVisible] = useState(false); // For storing state of back btn visibility
  const isMobile = useMediaQuery(ScreenSize.md);
  const auth: any = useAuth();
  const isLoggedIn: boolean = auth?.isAuthenticated;

  // Use cached profile image from IndexedDB
  const { profileImageUrl } = useProfileImage();
  const profileImagePath = profileImageUrl || profileIcon;

  /**
   * Handle back button click so that if back button is clicked from a page without history length, navigate the user to home
   */
  const handleBackBtnClick = () => {
    if (!isLoggedIn) {
      const isNavigationHandled = handleOnboardingBackNavigation(
        location,
        navigate,
      );
      if (isNavigationHandled) {
        return;
      }
    }
    // Check if there are previous pages
    if (window.history.length !== 1) {
      navigate(-1); // Navigate to previous page
    } else {
      navigate(routes.home.path); // Navigate to home
    }
  };

  useEffect(() => {
    // List of path where button should not be visible
    const BACK_BTN_IGNORE_LIST = [routes.home.path];
    const isPathIgnored = BACK_BTN_IGNORE_LIST.some((path) => {
      const pathWithSlash = path.startsWith("/") ? path : `/${path}`; // If the path from ignore list doesnot have "/" in the starting, add it, else keep it as is
      const isAllowed = pathWithSlash === location.pathname; // Compare with current location path
      return isAllowed;
    });
    if (!isPathIgnored) {
      setIsBackButtonVisible(true); // Set back button visibility true in non-ignored pages
    }
    // Reset the state when user navigates to a different path (pathname added as dependancy)
    return () => {
      setIsBackButtonVisible(false);
    };
  }, [location.pathname]);

  // Render only if the selected key matches the condition
  const showProgress: boolean =
    selectedKey_1 === routes.getGuidance.path &&
    selectedKey_3 !== routes.guidanceModule.path;

  return (
    <div
      className={`${styles.container_fluid} ${styles.header_container_wrapper}`}
    >
      <div className={styles.header_container}>
        {!isMobile && isBackButtonVisible && (
          <Button
            className={styles.back_button}
            onClick={handleBackBtnClick}
            type={BTN_VARIANTS.NEUTRAL}
            size={BTN_SIZE.NEUTRAL}
          >
            <Image
              source={backbutton}
              altText="back-arrow"
              className={styles.back_arrow_img}
            />
          </Button>
        )}
        <Link to={routes.home.path} className={styles.logo_img}>
          <Image source={logo} altText="Canesmeno" />
        </Link>
        {profileImagePath && (
          <div className={styles.right_menu}>
            <Link
              to={menuItems.PROFILE.to}
              className={styles.nav_item}
              onClick={() => {
                pushToDataLayer({
                  ...CONTENT.GENERAL.PROFILE,
                  user_status: isLoggedIn,
                });
              }}
            >
              <Image
                source={profileImagePath}
                altText={menuItems.PROFILE.value}
                className={profileImageUrl ? styles.user_set_image : ""}
              />
            </Link>
          </div>
        )}
        {/* only show progress bar in guidance details page */}
        {showProgress && (
          <div className={styles.guidance_progress}>{`${progressVal}%`}</div>
        )}
      </div>
      <div className={styles.gradient_container}></div>
    </div>
  );
};
